import { Feature, Geometry, Point } from "geojson";
import {
	DataDrivenPropertyValueSpecification,
	GeoJSONSource,
	LineLayerSpecification,
} from "mapbox-gl";
import { useContext, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { VelavuDevice } from "velavu-js-api";
import { geoJSONBlankCollection } from "./mapbox-injectable";
import * as styles from "./mapbox-injectable-anchors-routes.module.scss";
import {
	MapHelperInstance,
	useListenerSetupMapEffect,
	useStyleDependantMapEffect,
	useStyleSetupMapEffect,
} from "../../helper/map-helper";
import { createVelavuMapboxPopup } from "../../mapbox-component/velavu-mapbox-popup";
import { radToDeg } from "../../helper/math-helper";
import { distanceBetweenMercatorPoints } from "../../helper/geo-helper";
import { useSignInContext } from "../../components/sign-in-context/sign-in-context";
import {
	convertSmallDistance,
	getSmallDistanceUnit,
} from "../../helper/unit-helper";
import { hashStringToU32 } from "../../helper/hash-helper";
import whitelabel from "../../whitelabel";

const mapboxIDAnchorRouteLine = "anchor-route-lines";
const mapboxIDAnchorRouteLineFadeOut = "anchor-route-line-fade-out";
const mapboxIDAnchorRouteLineHoverArea = "anchor-route-lines-hover-area";
const mapboxIDAnchorRouteArrow = "anchor-route-arrow";

const lineColorDefault = whitelabel.palette.secondary500;
const lineColorHighlight = whitelabel.palette.primary900;

interface ConnectionPath {
	//A unique integer ID for this connection path
	integerID: number;
	//Coordinates of the source end of this path
	startCoords: [number, number];
	//Coordinates of the destination end of this path
	endCoords: [number, number];
	//Coordinates of the center of this path
	center: [number, number];
	//Angle from start to end in degrees
	angle: number;
	//Details about the device connection
	connectionDetails: ConnectionDetails;
	//Whether the end of this connection path should be hidden
	hideEnd: boolean;
}

interface ConnectionDetails {
	startDeviceID: string;
	endDeviceID: string;
	rssi: number;
	txPower: number;
	distance: number;
}

// We must extend ConnectionDetails because Mapbox does not support
// nested objects in GeoJSON properties.
// Note that only string and number values are supported as well.
interface ArrowProperties extends ConnectionDetails {
	rotation: number;
}

export interface MapboxInjectableAnchorsRoutesProps {
	// The map instance to apply this injectable to
	mapInstance: MapHelperInstance;
	// The anchors between which to draw lines
	anchors?: VelavuDevice[];
	// The zoom level at which to show the lines
	zoomThreshold?: number;
	// Any anchor IDs that should be hidden, creating fade-out lines
	hiddenAnchorIDs?: Set<string>;
	// The ID of the layer before which to insert this layer
	beforeLayer?: string;
	// Whether to ignore mouse interactions with this layer
	disableInteraction?: boolean;
}

/// A Mapbox layer that shows anchor routing lines
export default function useMapboxInjectableAnchorsRoutes(
	props: MapboxInjectableAnchorsRoutesProps,
) {
	const {
		mapInstance,
		anchors,
		zoomThreshold,
		hiddenAnchorIDs,
		beforeLayer,
		disableInteraction,
	} = props;

	const userContext = useSignInContext();
	const measurementSystem = userContext.preferences.measurementSystem;
	const { t } = useTranslation();

	const connectionPathArray = useMemo(() => {
		if (anchors === undefined) {
			return [];
		}

		return anchors
			.map((device): ConnectionPath | undefined => {
				//Get this device's coordinates
				const startCoords = device.location?.coordinates;
				if (startCoords === undefined) {
					return undefined;
				}

				//Get the ID of the device this device is routing to
				if (!device.state || !("routing" in device.state)) {
					return undefined;
				}
				const connectedDeviceID = device.state.routing.device_id;

				//Resolve if either end of the path is from a hidden anchor
				const startHidden = hiddenAnchorIDs?.has(device.id) ?? false;
				const endHidden =
					hiddenAnchorIDs?.has(connectedDeviceID) ?? false;

				//Don't show lines originating from anchors that are hidden
				if (startHidden) {
					return undefined;
				}

				//Look up that device
				const connectedDevice = anchors.find(
					(it) => it.id === connectedDeviceID,
				);
				if (connectedDevice === undefined) {
					return undefined;
				}

				//Get the connected device's coordinates
				const endCoords = connectedDevice.location?.coordinates;
				if (endCoords === undefined) {
					return undefined;
				}

				//Get the center of the path
				const center: [number, number] = [
					(startCoords[0] + endCoords[0]) / 2,
					(startCoords[1] + endCoords[1]) / 2,
				];

				//Calculate the difference angle
				const diffX = endCoords[0] - startCoords[0];
				const diffY = endCoords[1] - startCoords[1];
				const angle = radToDeg(Math.atan2(diffY, diffX));

				const details: ConnectionDetails = {
					startDeviceID: device.id,
					endDeviceID: connectedDeviceID,
					rssi: device.state.routing.rssi,
					txPower: device.state.routing.tx_power,
					distance: distanceBetweenMercatorPoints(
						startCoords,
						endCoords,
					),
				};

				return {
					integerID: hashStringToU32(device.id),
					startCoords: startCoords,
					endCoords: endCoords,
					center: center,
					angle: angle,
					connectionDetails: details,
					hideEnd: endHidden,
				};
			})
			.filter((it): it is ConnectionPath => it !== undefined);
	}, [anchors, hiddenAnchorIDs]);

	useStyleSetupMapEffect(mapInstance, (_, box) => {
		box.addSource(mapboxIDAnchorRouteLine, {
			type: "geojson",
			lineMetrics: true,
			data: geoJSONBlankCollection,
		});

		box.addSource(mapboxIDAnchorRouteArrow, {
			type: "geojson",
			data: geoJSONBlankCollection,
		});

		const COMMON_PAINT: Readonly<LineLayerSpecification["paint"]> = {
			"line-width": ["step", ["zoom"], 0, 18, 1, 19, 2, 20, 3],
			"line-dasharray": [
				"step",
				["zoom"],
				["literal", [0, 2]],
				18,
				["literal", [0, 2]],
				18.5,
				["literal", [0, 2.5]],
				19,
				["literal", [0, 3]],
				19.5,
				["literal", [0, 3.5]],
				20,
				["literal", [0, 4]],
				20.5,
				["literal", [0, 4.5]],
				21,
				["literal", [0, 5]],
				21.5,
				["literal", [0, 5.5]],
				22,
				["literal", [0, 6]],
			],
			"line-color": [
				"case",
				["boolean", ["feature-state", "hover"], false],
				lineColorHighlight,
				lineColorDefault,
			],
		};

		box.addLayer(
			{
				id: mapboxIDAnchorRouteLine,
				type: "line",
				source: mapboxIDAnchorRouteLine,
				filter: ["!", ["get", "hideEnd"]],
				layout: {
					"line-cap": "round",
					"line-join": "round",
				},
				paint: COMMON_PAINT,
			},
			beforeLayer,
		);

		box.addLayer(
			{
				id: mapboxIDAnchorRouteLineFadeOut,
				type: "line",
				source: mapboxIDAnchorRouteLine,
				filter: ["get", "hideEnd"],
				layout: {
					"line-cap": "round",
					"line-join": "round",
				},
				paint: {
					...COMMON_PAINT,
					"line-trim-fade-range": [1.0, 0.0],
					//Setting the first offset value to 0 makes the line disappear for some reason
					"line-trim-offset": [0.01, 1.0],
				},
			},
			beforeLayer,
		);

		//Create a thicker transparent line to use as a hover target
		box.addLayer({
			id: mapboxIDAnchorRouteLineHoverArea,
			type: "line",
			source: mapboxIDAnchorRouteLine,
			paint: {
				"line-opacity": 0,
				"line-width": 18,
			},
		});

		box.addLayer(
			{
				id: mapboxIDAnchorRouteArrow,
				type: "symbol",
				source: mapboxIDAnchorRouteArrow,
				layout: {
					"icon-image": "icon-routing-arrow",
					"icon-size": 0.2,
					"icon-rotate": ["get", "rotation"],
					"icon-allow-overlap": true,
					"icon-ignore-placement": true,
				},
				paint: {
					"icon-color": [
						"case",
						["boolean", ["feature-state", "hover"], false],
						whitelabel.palette.primary900,
						whitelabel.palette.secondary500,
					],
				},
			},
			beforeLayer,
		);
	});

	const routePopup = useMemo(
		() =>
			createVelavuMapboxPopup(
				{
					className: styles.tooltipContainer,
					offset: [0, -10],
					anchor: "bottom",
					maxWidth: "400px",
				},
				true,
			),
		[],
	);

	//Update zoom threshold
	useStyleDependantMapEffect(
		mapInstance,
		(map) => {
			let expression: DataDrivenPropertyValueSpecification<number>;
			if (zoomThreshold !== undefined) {
				expression = [
					"step",
					["zoom"],
					0,
					zoomThreshold - 1,
					0,
					zoomThreshold,
					1,
				];
			} else {
				expression = 1;
			}

			map.setPaintProperty(
				mapboxIDAnchorRouteLine,
				"line-opacity",
				expression,
			);
			map.setPaintProperty(
				mapboxIDAnchorRouteLineFadeOut,
				"line-opacity",
				expression,
			);
			map.setPaintProperty(
				mapboxIDAnchorRouteArrow,
				"icon-opacity",
				expression,
			);
		},
		[zoomThreshold],
	);

	//Update line source
	useStyleDependantMapEffect(
		mapInstance,
		(map) => {
			const lineSource = map.getSource(
				mapboxIDAnchorRouteLine,
			) as GeoJSONSource;

			lineSource.setData({
				type: "FeatureCollection",
				features: connectionPathArray.map((path) => ({
					type: "Feature",
					id: path.integerID,
					geometry: {
						type: "LineString",
						coordinates: [path.startCoords, path.endCoords],
					},
					properties: {
						hideEnd: path.hideEnd,
					},
				})),
			});
		},
		[connectionPathArray],
	);

	//Update arrow source
	useStyleDependantMapEffect(
		mapInstance,
		(map) => {
			const arrowSource = map.getSource(
				mapboxIDAnchorRouteArrow,
			) as GeoJSONSource;

			arrowSource.setData({
				type: "FeatureCollection",
				features: connectionPathArray.map<
					Feature<Geometry, ArrowProperties>
				>((path) => ({
					type: "Feature",
					id: path.integerID,
					geometry: {
						type: "Point",
						coordinates: path.center,
					},
					properties: {
						...path.connectionDetails,
						rotation: path.angle * -1,
					},
				})),
			});
		},
		[connectionPathArray],
	);

	//Track line hover state
	const hoveredConnectionID = useRef<number | null>(null);
	useListenerSetupMapEffect(
		mapInstance.map,
		(map, box) => {
			if (disableInteraction) return;

			box.on("mousemove", mapboxIDAnchorRouteLineHoverArea, (event) => {
				if (event.features !== undefined && event.features.length > 0) {
					//Untrack the last hovered connection
					if (hoveredConnectionID.current !== null) {
						map.setFeatureState(
							{
								source: mapboxIDAnchorRouteLine,
								id: hoveredConnectionID.current,
							},
							{
								hover: false,
							},
						);

						map.setFeatureState(
							{
								source: mapboxIDAnchorRouteArrow,
								id: hoveredConnectionID.current,
							},
							{
								hover: false,
							},
						);
					}

					//Set the new hovered connection line
					const hoveredID = event.features[0].id as number;
					map.setFeatureState(
						{
							source: mapboxIDAnchorRouteLine,
							id: hoveredID,
						},
						{
							hover: true,
						},
					);
					map.setFeatureState(
						{
							source: mapboxIDAnchorRouteArrow,
							id: hoveredID,
						},
						{
							hover: true,
						},
					);

					hoveredConnectionID.current = hoveredID;
				}
			});

			const clearHover = () => {
				if (hoveredConnectionID.current !== null) {
					map.setFeatureState(
						{
							source: mapboxIDAnchorRouteLine,
							id: hoveredConnectionID.current,
						},
						{
							hover: false,
						},
					);
					map.setFeatureState(
						{
							source: mapboxIDAnchorRouteArrow,
							id: hoveredConnectionID.current,
						},
						{
							hover: false,
						},
					);
					hoveredConnectionID.current = null;
				}
			};
			box.on("mouseleave", mapboxIDAnchorRouteLineHoverArea, clearHover);

			//Clear the hover state, since we won't get a mouseleave
			//event once the listeners are removed
			return clearHover;
		},
		[disableInteraction],
	);

	//Show popup on arrow hover
	useListenerSetupMapEffect(
		mapInstance.map,
		(map, box) => {
			if (disableInteraction) return;

			box.on("mouseenter", mapboxIDAnchorRouteArrow, (event) => {
				const feature = event.features![0];
				const properties = feature.properties as ArrowProperties;
				const connection: ConnectionDetails = properties;

				const formattedDistance = convertSmallDistance(
					properties.distance,
					measurementSystem,
				).toFixed(1);
				const distanceUnit = getSmallDistanceUnit(measurementSystem);
				const dbmUnit = t("api_helper.dbm_unit");
				const signalStrengthLabel = t(
					"dashboard.anchor_route.signal_strength",
				);
				const txPowerLabel = t("dashboard.anchor_route.tx_power");
				const distanceOnMapLabel = t(
					"dashboard.anchor_route.distance_on_map",
				);

				//Change the signal strength box color depending on the value
				let signalStrengthBoxClass: string;
				if (connection.rssi > -74) {
					signalStrengthBoxClass = styles.infoBoxGreen;
				} else if (connection.rssi > -84) {
					signalStrengthBoxClass = styles.infoBoxYellow;
				} else {
					signalStrengthBoxClass = styles.infoBoxRed;
				}

				const anchorSvg = `
				<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path fill-rule="evenodd" clip-rule="evenodd" d="M10.6665 4.66732H5.33317C3.49222 4.66732 1.99984 6.1597 1.99984 8.00065C1.99984 9.8416 3.49222 11.334 5.33317 11.334H10.6665C12.5075 11.334 13.9998 9.8416 13.9998 8.00065C13.9998 6.1597 12.5075 4.66732 10.6665 4.66732ZM5.33317 3.33398C2.75584 3.33398 0.666504 5.42332 0.666504 8.00065C0.666504 10.578 2.75584 12.6673 5.33317 12.6673H10.6665C13.2438 12.6673 15.3332 10.578 15.3332 8.00065C15.3332 5.42332 13.2438 3.33398 10.6665 3.33398H5.33317Z" fill="${whitelabel.palette.primary900}"/>
					<path fill-rule="evenodd" clip-rule="evenodd" d="M10.6665 7.33398H5.33317C4.96498 7.33398 4.6665 7.63246 4.6665 8.00065C4.6665 8.36884 4.96498 8.66732 5.33317 8.66732H10.6665C11.0347 8.66732 11.3332 8.36884 11.3332 8.00065C11.3332 7.63246 11.0347 7.33398 10.6665 7.33398ZM5.33317 6.00065C4.2286 6.00065 3.33317 6.89608 3.33317 8.00065C3.33317 9.10522 4.2286 10.0007 5.33317 10.0007H10.6665C11.7711 10.0007 12.6665 9.10522 12.6665 8.00065C12.6665 6.89608 11.7711 6.00065 10.6665 6.00065H5.33317Z" fill="${whitelabel.palette.primary900}"/>
				</svg>
			`;

				const arrowSvg = `
				<svg width="26" height="16" viewBox="0 0 26 16" fill="none" xmlns="http://www.w3.org/2000/svg">
					<rect x="3" y="7" width="15" height="2" rx="1" fill="${whitelabel.palette.secondary500}"/>
					<path d="M23 7.13397C23.6667 7.51887 23.6667 8.48113 23 8.86603L17 12.3301C16.3333 12.715 15.5 12.2339 15.5 11.4641L15.5 4.5359C15.5 3.7661 16.3333 3.28497 17 3.66987L23 7.13397Z" fill="${whitelabel.palette.secondary500}"/>
				</svg>
			`;

				const descriptionHTML = `
				<div class="${styles.routeInfo}">
					<span class="${styles.routeInfoEntry}">
						${anchorSvg}
						<span>${connection.startDeviceID}</span>
					</span>
					${arrowSvg}
					<span class="${styles.routeInfoEntry}">
						${anchorSvg}
						<span>${connection.endDeviceID}</span>
					</span>
				</div>

				<div class="${styles.infoRow}">
					<div class="${styles.infoBox} ${signalStrengthBoxClass}">
						${connection.rssi}
					</div>
					<span class="${styles.infoPrimary}">${signalStrengthLabel}</span>
					<span class="${styles.infoSecondary}">(${dbmUnit})</span>
				</div>
				<div class="${styles.infoRow}">
					<div class="${styles.infoBox}">
						${connection.txPower}
						</div>
					<span class="${styles.infoPrimary}">${txPowerLabel}</span>
					<span class="${styles.infoSecondary}">(${dbmUnit})</span>
				</div>
				<div class="${styles.infoRow}">
					<div class="${styles.infoBox}">
						${formattedDistance}
					</div>
					<span class="${styles.infoPrimary}">${distanceOnMapLabel}</span>
					<span class="${styles.infoSecondary}">(${distanceUnit})</span>
				</div>
			`;
				const coordinates = (
					feature.geometry as Point
				).coordinates.slice() as [number, number];
				routePopup
					.setLngLat(coordinates)
					.setHTML(descriptionHTML)
					.addTo(map);
			});

			const onMouseLeave = () => {
				routePopup.remove();
			};
			box.on("mouseleave", mapboxIDAnchorRouteArrow, onMouseLeave);
			return onMouseLeave;
		},
		[routePopup, measurementSystem, t, disableInteraction],
	);

	return mapboxIDAnchorRouteLine;
}
